<template>
  <div class="seller-management">
    <div v-if="!hasSupportAccess" class="access-denied-card">
      <div class="access-denied-icon"><i class="fa-solid fa-lock"></i></div>
      <h3>Access restricted</h3>
      <p>Only super admin or support can manage sellers. Your role does not have permission.</p>
      <p class="access-denied-hint">Sign in with an admin account that has access to seller tools.</p>
    </div>

    <div v-else>
    <!-- Header -->
    <div class="page-header">
      <h1>Seller Management</h1>
      <p>Browse sellers and linked customer accounts. Use customer suspension to restrict marketplace activity; seller listing and earnings APIs require an active customer account.</p>
    </div>

    <!-- Filters and Stats -->
    <div class="filters-section">
      <div class="stats-cards">
        <div class="stat-card total">
          <div class="stat-icon">
            <i class="fa-solid fa-store"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.total }}</h3>
            <p>Total Sellers</p>
          </div>
        </div>
        <div class="stat-card approved">
          <div class="stat-icon">
            <i class="fa-solid fa-shield-check"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.verified }}</h3>
            <p>Verified Sellers</p>
          </div>
        </div>
        <div class="stat-card pending">
          <div class="stat-icon">
            <i class="fa-solid fa-user-plus"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.new_sellers_30_days }}</h3>
            <p>New (30 days)</p>
          </div>
        </div>
        <div class="stat-card rejected">
          <div class="stat-icon">
            <i class="fa-solid fa-user-slash"></i>
          </div>
          <div class="stat-content">
            <h3>{{ stats.sellers_with_suspended_customer }}</h3>
            <p>Suspended customer</p>
          </div>
        </div>
      </div>

      <div class="filter-controls">
        <div class="filter-group">
          <label>Customer account:</label>
          <select v-model="filters.customerAccount" @change="loadSellers">
            <option value="all">All</option>
            <option value="active">Active (not suspended)</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Verification:</label>
          <select v-model="filters.verified" @change="loadSellers">
            <option value="all">All</option>
            <option value="verified">Verified</option>
            <option value="unverified">Unverified</option>
          </select>
        </div>

        <div class="search-box">
          <i class="fa-solid fa-search"></i>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search sellers by name, email, or seller number..."
            @input="debouncedSearch"
          >
        </div>

        <button class="btn btn-secondary" @click="resetFilters">
          <i class="fa-solid fa-refresh"></i>
          Reset Filters
        </button>
      </div>
    </div>

    <!-- Sellers Table -->
    <div class="sellers-table-container">
      <div class="table-header">
        <h3>Sellers</h3>
      </div>

      <div class="table-wrapper">
        <table class="sellers-table">
          <thead>
            <tr>
              <th>Seller Info</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Status</th>
              <th>Registration Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="seller in paginatedSellers" :key="seller.seller_number">
              <td class="seller-info">
                <div class="seller-avatar">
                  <img
                    v-if="seller.profile_picture"
                    :src="seller.profile_picture"
                    :alt="seller.full_name"
                    loading="lazy"
                    decoding="async"
                  >
                  <div v-else class="avatar-placeholder">
                    {{ getUserInitials(seller.full_name) }}
                  </div>
                </div>
                <div class="seller-details">
                  <strong>{{ seller.full_name }}</strong>
                  <span class="seller-email">{{ seller.email }}</span>
                  <span class="seller-number">#{{ seller.seller_number }}</span>
                </div>
              </td>
              <td class="contact-info">
                <div class="contact-item">
                  <i class="fa-solid fa-phone"></i>
                  <span>{{ seller.phone }}</span>
                </div>
                <div class="contact-item" v-if="seller.social">
                  <i class="fa-solid fa-link"></i>
                  <span class="social-link">{{ seller.social }}</span>
                </div>
              </td>
              <td class="location-info">
                <i class="fa-solid fa-location-dot"></i>
                <span>{{ seller.location }}</span>
              </td>
              <td class="status-info">
                <div class="status-badges">
                  <span
                    class="customer-account-badge"
                    :class="isCustomerSuspended(seller) ? 'suspended' : 'active'"
                  >
                    <i :class="isCustomerSuspended(seller) ? 'fa-solid fa-ban' : 'fa-solid fa-circle-check'"></i>
                    {{ isCustomerSuspended(seller) ? 'Customer suspended' : 'Customer active' }}
                  </span>
                  <span v-if="seller.is_verified" class="verification-badge verified">
                    <i class="fa-solid fa-shield-check"></i>
                    Verified
                  </span>
                  <span v-else class="verification-badge unverified">
                    <i class="fa-solid fa-shield"></i>
                    Unverified
                  </span>
                </div>
              </td>
              <td class="registration-date">
                {{ formatDate(seller.created_at) }}
              </td>
              <td class="actions">
                <button 
                  class="btn btn-sm btn-info" 
                  @click="viewSellerDetails(seller)"
                  title="View Details"
                >
                  <i class="fa-solid fa-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div v-if="sellers.length === 0" class="empty-state">
          <i class="fa-solid fa-store-slash"></i>
          <h4>No sellers found</h4>
          <p>There are no sellers matching your current filters.</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="sellers.length > 0">
        <button 
          class="btn btn-outline" 
          @click="previousPage"
          :disabled="currentPage === 1"
        >
          <i class="fa-solid fa-chevron-left"></i>
          Previous
        </button>
        
        <span class="page-info">
          Page {{ currentPage }} of {{ totalPages }} ({{ sellers.length }} total sellers)
        </span>
        
        <button 
          class="btn btn-outline" 
          @click="nextPage"
          :disabled="currentPage === totalPages"
        >
          Next
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Seller Details Modal -->
    <div v-if="selectedSeller" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Seller Details - {{ selectedSeller.full_name }}</h3>
          <button class="close-btn" @click="closeModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="seller-details-grid">
            <!-- Basic Information -->
            <div class="detail-section">
              <h4>Basic Information</h4>
              <div class="seller-profile">
                <div class="profile-avatar">
                  <img
                    v-if="selectedSeller.profile_picture"
                    :src="selectedSeller.profile_picture"
                    :alt="selectedSeller.full_name"
                    loading="lazy"
                    decoding="async"
                  >
                  <div v-else class="avatar-placeholder large">
                    {{ getUserInitials(selectedSeller.full_name) }}
                  </div>
                </div>
                <div class="profile-info">
                  <h5>{{ selectedSeller.full_name }}</h5>
                  <p class="seller-email">{{ selectedSeller.email }}</p>
                  <p class="seller-number">Seller #{{ selectedSeller.seller_number }}</p>
                </div>
              </div>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Full name:</label>
                  <span>{{ selectedSeller.full_name || '—' }}</span>
                </div>
                <div class="detail-item">
                  <label>Seller Number:</label>
                  <span>#{{ selectedSeller.seller_number }}</span>
                </div>
                <div class="detail-item">
                  <label>Customer ID:</label>
                  <span>#{{ selectedSeller.customer_id }}</span>
                </div>
                <div class="detail-item">
                  <label>Email:</label>
                  <span>{{ selectedSeller.email }}</span>
                </div>
                <div class="detail-item">
                  <label>Phone:</label>
                  <span>{{ selectedSeller.phone }}</span>
                </div>
              </div>
            </div>

            <!-- Business Information -->
            <div class="detail-section">
              <h4>Business Information</h4>
              <div class="detail-grid">
                <div class="detail-item full-width">
                  <label>Location:</label>
                  <span>{{ selectedSeller.location }}</span>
                </div>
                <div class="detail-item full-width" v-if="selectedSeller.social">
                  <label>Social/Website:</label>
                  <a :href="selectedSeller.social" target="_blank" class="social-link">
                    {{ selectedSeller.social }}
                  </a>
                </div>
              </div>
            </div>

            <!-- Account Status -->
            <div class="detail-section">
              <h4>Account Status</h4>
              <div class="status-grid">
                <div class="status-item">
                  <label>Customer account:</label>
                  <span
                    class="customer-account-badge"
                    :class="isCustomerSuspended(selectedSeller) ? 'suspended' : 'active'"
                  >
                    {{ formatCustomerAccountStatus(selectedSeller.customer_account_status) }}
                  </span>
                </div>
                <div class="status-item">
                  <label>Seller record (legacy):</label>
                  <span class="status-badge muted">{{ selectedSeller.approval_status || '—' }}</span>
                </div>
                <div class="status-item">
                  <label>Verification:</label>
                  <span class="verification-badge" :class="selectedSeller.is_verified ? 'verified' : 'unverified'">
                    <i class="fa-solid" :class="selectedSeller.is_verified ? 'fa-check' : 'fa-times'"></i>
                    {{ selectedSeller.is_verified ? 'Verified' : 'Unverified' }}
                  </span>
                </div>
                <div class="status-item">
                  <label>Registration Date:</label>
                  <span>{{ formatDate(selectedSeller.created_at) }}</span>
                </div>
                <div class="status-item">
                  <label>Last Updated:</label>
                  <span>{{ formatDate(selectedSeller.updated_at) }}</span>
                </div>
              </div>
            </div>

            <!-- Customer Information -->
            <div class="detail-section">
              <h4>Customer Information</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Customer Since:</label>
                  <span>{{ formatDate(selectedSeller.customer_created_at) }}</span>
                </div>
                <div class="detail-item">
                  <label>Total Purchases:</label>
                  <span>{{ selectedSeller.customer_total_purchases || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-outline" @click="closeModal">
            Close
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { useAdminAuthStore } from "@/authen/adminAuth";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import globalToastMixin from "@/mixins/globalToastMixin";
import { buildApiUrl } from "@/config/api";

export default {
  name: 'SellerManagement',
  loadingMessage: 'Loading sellers...',
  mixins: [globalToastMixin, globalLoadingMixin],

  data() {
    return {
      loading: false,
      sellers: [],
      selectedSeller: null,
      searchQuery: '',
      filters: {
        customerAccount: 'all',
        verified: 'all'
      },
      stats: {
        total: 0,
        verified: 0,
        new_sellers_30_days: 0,
        sellers_with_suspended_customer: 0,
      },
      pagination: {
        currentPage: 1,
        pageSize: 10
      },
      debounceTimer: null,
    }
  },

  computed: {
    hasSupportAccess() {
      const store = useAdminAuthStore();
      return store.canAccessSupportAdminTools;
    },
    paginatedSellers() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.sellers.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.sellers.length / this.pagination.pageSize);
    },
    currentPage() {
      return this.pagination.currentPage;
    }
  },

  mounted() {
    const store = useAdminAuthStore();
    store.checkToken();
    if (!this.hasSupportAccess) return;
    this.loadSellers();
    this.loadStats();
  },

  beforeUnmount() {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  },

  methods: {
    getAdminHeaders(json = false) {
      const store = useAdminAuthStore();
      store.checkToken();
      const headers = { Accept: "application/json" };
      if (json) headers["Content-Type"] = "application/json";
      const token = store.accessToken;
      if (token) headers["Authorization"] = `Bearer ${token}`;
      return headers;
    },

    async loadSellers() {
      if (!this.hasSupportAccess) return;
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.filters.customerAccount !== 'all') {
          params.append('customer_account', this.filters.customerAccount);
        }
        if (this.filters.verified !== 'all') params.append('verified', this.filters.verified);
        if (this.searchQuery) params.append('search', this.searchQuery);

        const response = await fetch(buildApiUrl(`/seller/admin/get_all_sellers?${params.toString()}`), {
          headers: this.getAdminHeaders(false),
        });
        
        if (!response.ok) throw new Error('Failed to load sellers');
        this.sellers = await response.json();
      } catch (error) {
        console.error('Error loading sellers:', error);
        this.showToast('error', 'Load failed', 'Could not load sellers. Check your connection and admin session.');
      } finally {
        this.loading = false;
      }
    },

    async loadStats() {
      if (!this.hasSupportAccess) return;
      try {
        const response = await fetch(buildApiUrl("/seller/admin/stats"), {
          headers: this.getAdminHeaders(false),
        });
        
        if (!response.ok) throw new Error('Failed to load stats');
        const data = await response.json();
        this.stats = {
          total: data.total ?? 0,
          verified: data.verified ?? 0,
          new_sellers_30_days: data.new_sellers_30_days ?? 0,
          sellers_with_suspended_customer: data.sellers_with_suspended_customer ?? 0,
        };
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.loadSellers();
      }, 500);
    },

    getUserInitials(fullName) {
      if (!fullName || typeof fullName !== 'string') return '?';
      return fullName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    isCustomerSuspended(seller) {
      if (!seller) return false;
      const s = (seller.customer_account_status || '').toString().trim().toLowerCase();
      return s === 'suspended';
    },

    formatCustomerAccountStatus(status) {
      const s = (status || 'active').toString().trim().toLowerCase();
      if (s === 'suspended') return 'Suspended';
      if (!s || s === 'active') return 'Active';
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    async viewSellerDetails(seller) {
      try {
        const response = await fetch(buildApiUrl(`/seller/admin/${seller.seller_number}`), {
          headers: this.getAdminHeaders(false),
        });
        if (response.ok) {
          const detail = await response.json();
          this.selectedSeller = {
            ...detail,
            ...seller,
            full_name: detail.full_name || seller.full_name,
            profile_picture: seller.profile_picture || detail.profile_picture,
            email: detail.email || seller.email,
            phone: detail.phone || seller.phone,
            location: detail.location || seller.location,
            social: detail.social != null ? detail.social : seller.social,
            customer_account_status: detail.customer_account_status ?? seller.customer_account_status,
          };
        } else {
          this.selectedSeller = { ...seller };
          this.showToast('warning', 'Details', 'Could not refresh seller details from the server.');
        }
      } catch (e) {
        console.error(e);
        this.selectedSeller = { ...seller };
        this.showToast('warning', 'Details', 'Could not load full seller details.');
      }
    },

    closeModal() {
      this.selectedSeller = null;
    },

    resetFilters() {
      this.filters = {
        customerAccount: 'all',
        verified: 'all'
      };
      this.searchQuery = '';
      this.loadSellers();
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.pagination.currentPage++;
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.pagination.currentPage--;
      }
    }
  }
}
</script>

<style scoped>
.access-denied-card {
  max-width: 420px;
  margin: 60px auto;
  padding: 32px;
  background: var(--background-color2);
  border-radius: 12px;
  border: 1px solid var(--border-color2);
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.access-denied-icon {
  font-size: 48px;
  color: #f59e0b;
  margin-bottom: 16px;
}
.access-denied-card h3 {
  font-size: 1.25rem;
  color: var(--text-color1);
  margin: 0 0 12px 0;
}
.access-denied-card p {
  font-size: 0.9375rem;
  color: var(--text-color2);
  margin: 0 0 8px 0;
  line-height: 1.5;
}
.access-denied-hint {
  font-size: 0.8125rem;
  opacity: 0.85;
}

.customer-account-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}
.customer-account-badge.active {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.35);
}
.customer-account-badge.suspended {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.35);
}
.status-badge.muted {
  opacity: 0.85;
  text-transform: capitalize;
  background: var(--background-color2);
  color: var(--text-color2);
  border: 1px solid var(--border-color2);
}

.seller-management {
  padding: 2rem;
  background: var(--background-color1);
  min-height: 100vh;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: var(--text-color1);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--text-color2);
}

/* Stats Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--background-color2);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stat-icon {
  font-size: 2rem;
  margin-right: 1rem;
}

.stat-card.pending .stat-icon { color: #ffc107; }
.stat-card.approved .stat-icon { color: #28a745; }
.stat-card.rejected .stat-icon { color: #dc3545; }
.stat-card.total .stat-icon { color: #007bff; }

.stat-content h3 {
  font-size: 1.5rem;
  margin: 0;
  color: var(--text-color1);
}

.stat-content p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.9rem;
}

/* Filter Controls */
.filter-controls {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--background-color2);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.9rem;
}

.filter-group select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: var(--background-color1);
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: var(--background-color1);
}

/* Table Styles */
.sellers-table-container {
  background: var(--background-color2);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
}

.table-wrapper {
  overflow-x: auto;
}

.sellers-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px;
}

.sellers-table th,
.sellers-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.sellers-table th {
  background: var(--background-color2);
  font-weight: 600;
  color: var(--text-color1);
  position: sticky;
  top: 0;
}

.sellers-table tbody tr:hover {
  background: #f8f9fa;
}

/* Specific column styles */
.seller-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.seller-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.seller-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: var(--border-color-hover);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.avatar-placeholder.large {
  width: 80px;
  height: 80px;
  font-size: 1.5rem;
}

.seller-details {
  display: flex;
  flex-direction: column;
}

.seller-details strong {
  color: var(--text-color1);
  margin-bottom: 0.25rem;
}

.seller-email {
  font-size: 0.8rem;
  color: var(--text-color2);
}

.seller-number {
  font-size: 0.7rem;
  color: var(--text-color2);
}

.contact-info {
  min-width: 150px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.8rem;
}

.contact-item i {
  width: 12px;
  color: var(--text-color2);
}

.social-link {
  color: var(--border-color-hover);
  text-decoration: none;
}

.social-link:hover {
  text-decoration: underline;
}

.location-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
}

.status-info {
  min-width: 120px;
}

.status-badges {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.status-badge {
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
  display: inline-block;
  text-align: center;
}

.status-badge.pending {
  background: #fff3cd;
  color: #856404;
}

.status-badge.approved {
  background: #d4edda;
  color: #155724;
}

.status-badge.rejected {
  background: #f8d7da;
  color: #721c24;
}

.verification-badge {
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.verification-badge.verified {
  background: #d1ecf1;
  color: #0c5460;
}

.verification-badge.unverified {
  background: #f8d7da;
  color: #721c24;
}

.registration-date {
  font-size: 0.8rem;
  color: var(--text-color2);
}

.actions {
  display: flex;
  gap: 0.25rem;
}

.actions .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--text-color2);
}

.modal-body {
  padding: 1.5rem;
}

.seller-details-grid {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.detail-section h4 {
  margin-bottom: 1rem;
  color: var(--text-color1);
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.seller-profile {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-info h5 {
  margin: 0 0 0.25rem 0;
  font-size: 1.2rem;
  color: var(--text-color1);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-item label {
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.9rem;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.modal-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid #eee;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-color2);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #ddd;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--border-color-hover);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-top: 1px solid #eee;
}

.page-info {
  color: var(--text-color2);
  font-weight: 600;
}

/* Toast */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1100;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 380px;
  padding: 14px 16px;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  background: var(--background-color2);
  border: 1px solid var(--border-color2);
}
.toast-notification.success {
  border-left: 4px solid #22c55e;
}
.toast-notification.error {
  border-left: 4px solid #ef4444;
}
.toast-notification.warning {
  border-left: 4px solid #f59e0b;
}
.toast-notification.info {
  border-left: 4px solid #3b82f6;
}
.toast-notification i {
  font-size: 1.25rem;
  margin-top: 2px;
}
.toast-content {
  flex: 1;
  min-width: 0;
}
.toast-title {
  margin: 0 0 4px 0;
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--text-color1);
}
.toast-message {
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-color2);
  line-height: 1.4;
}
.toast-close {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-color2);
  opacity: 0.7;
}
.toast-close:hover {
  opacity: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .seller-management {
    padding: 1rem;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: auto;
  }

  .table-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .table-actions {
    justify-content: stretch;
  }

  .table-actions .btn {
    flex: 1;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .stats-cards {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}
</style>